import {
  QueryClient,
  QueryClientConfig,
  QueryClientProvider,
} from '@tanstack/react-query';
import React, { ReactNode, useMemo } from 'react';
import {
  QueryClient as LegacyQueryClient,
  QueryClientConfig as LegacyQueryClientConfig,
  QueryClientProvider as LegacyQueryClientProvider,
} from 'react-query';

// Default configuration for query clients
const defaultQueryClientOptions = {
  defaultOptions: {
    queries: {
      retry: false,
      retryOnMount: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    },
  },
};

interface ReactQueryProvidersProps {
  /** Child components to be wrapped by the providers */
  children: ReactNode;
  /** Optional custom TanStack QueryClient */
  queryClient?: QueryClient;
  /** Optional custom legacy React Query client */
  legacyQueryClient?: LegacyQueryClient;
  /** Optional custom options for creating query clients */
  queryClientOptions?: QueryClientConfig;
  /** Optional custom options for creating legacy query clients */
  legacyQueryClientOptions?: LegacyQueryClientConfig;
}

/**
 * A component that provides both TanStack React Query v4 and legacy React Query v3 providers
 * for use during migration.
 */
const ReactQueryProviders: React.FC<ReactQueryProvidersProps> = ({
  children,
  queryClient: customQueryClient,
  legacyQueryClient: customLegacyQueryClient,
  queryClientOptions = defaultQueryClientOptions,
  legacyQueryClientOptions = defaultQueryClientOptions,
}) => {
  // Use provided query clients or create new ones
  const queryClient = useMemo(
    () => customQueryClient ?? new QueryClient(queryClientOptions),
    [customQueryClient, queryClientOptions],
  );

  const legacyQueryClient = useMemo(
    () =>
      customLegacyQueryClient ??
      new LegacyQueryClient(legacyQueryClientOptions),
    [customLegacyQueryClient, legacyQueryClientOptions],
  );

  return (
    <QueryClientProvider client={queryClient}>
      <LegacyQueryClientProvider client={legacyQueryClient}>
        {children}
      </LegacyQueryClientProvider>
    </QueryClientProvider>
  );
};

export default React.memo(ReactQueryProviders);
